import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { apiClient } from '../api';
import { ensureServiceWorker, isPushSupported } from '../lib/push';
import { useSharedWebSocket } from './useSharedWebSocket';
import { captureRefFromUrl, readStoredRef, clearStoredRef } from '../lib/referrals';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Owns the single WS instance for the whole app, scoped to user identity.
  const { connected: wsConnected, subscribe: wsSubscribe, send: wsSend, close: wsClose } =
    useSharedWebSocket(user?.id);

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch (err) {
      // Unauthenticated or network blip — treat as logged-out and continue.
      console.debug('Auth refresh: no active session', err?.response?.status);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setSession = useCallback((data) => {
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password) => {
    const ref = readStoredRef();
    const res = await apiClient.post('/auth/register', { email, password, ref });
    if (ref) clearStoredRef();
    return setSession(res.data);
  }, [setSession]);

  const login = useCallback(async (email, password) => {
    const res = await apiClient.post('/auth/login', { email, password });
    return setSession(res.data);
  }, [setSession]);

  const googleLogin = useCallback(async (sessionId) => {
    const res = await apiClient.post('/auth/google/session', { session_id: sessionId });
    return setSession(res.data);
  }, [setSession]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (err) {
      // Server-side logout failure is non-fatal — local state is the source
      // of truth for the client. Log for debugging only.
      console.debug('Logout endpoint failed (continuing client-side):', err?.message);
    }
    // Defensive: clear any pre-migration legacy localStorage entries.
    try { localStorage.removeItem('fnl_token'); } catch (err) {
      console.debug('localStorage cleanup skipped:', err?.message);
    }
    setUser(null);
    wsClose();
  }, [wsClose]);

  // Register the service worker once at app startup so push notifications
  // can be enabled later. We do NOT prompt for permission here — that's a
  // user-initiated action via the Push toggle.
  useEffect(() => {
    if (!isPushSupported()) return;
    ensureServiceWorker().catch((err) => {
      console.warn('Service worker registration failed:', err?.message || err);
    });
  }, []);

  // Capture `?ref=...` from the landing URL on first mount so the new
  // visitor's eventual signup carries the referral linkage.
  useEffect(() => { captureRefFromUrl(); }, []);

  const value = useMemo(
    () => ({
      user, loading, wsConnected,
      register, login, googleLogin, logout, refresh, setUser,
      wsSubscribe, wsSend,
    }),
    [user, loading, wsConnected, register, login, googleLogin, logout, refresh, wsSubscribe, wsSend]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
